import React, { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  MapPin, AlertTriangle, CheckCircle2, Circle, Clock, Ban, Plus, Trash2,
  FileText, RefreshCw, MessageSquare, ArrowLeft, ExternalLink,
} from 'lucide-react';
import { neonQuery } from '../services/api';
import { getCurrentUser } from '../services/auth';

const STATUSES = [
  { key: 'not_started', label: 'Not started', icon: Circle, color: 'var(--navy)', bg: '#eef2f6' },
  { key: 'in_progress', label: 'In progress', icon: Clock, color: '#8a5a00', bg: '#fff4dd' },
  { key: 'blocked', label: 'Blocked', icon: Ban, color: '#a12020', bg: '#fdecec' },
  { key: 'done', label: 'Done', icon: CheckCircle2, color: '#1c6b3f', bg: '#e6f5ec' },
];
const statusMeta = (k) => STATUSES.find((s) => s.key === k) || STATUSES[0];
const nextStatus = (k) => {
  const i = STATUSES.findIndex((s) => s.key === k);
  return STATUSES[(i + 1) % STATUSES.length].key;
};

const OWNERS = ['', 'Greg', 'Bret Davis', 'Becker Morgan', 'DHCD', 'City of Crisfield',
  'Somerset County', 'FEMA / MDEM', 'Claude'];

// due_date arrives as a plain 'YYYY-MM-DD' string from to_char(). Parse it as a
// LOCAL date - new Date('2026-08-20') is read as UTC midnight and renders as the
// day before in Eastern, which would show everything a day early.
const parseYmd = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || ''));
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
};
const startOfToday = () => {
  const n = new Date();
  return new Date(n.getFullYear(), n.getMonth(), n.getDate());
};
// Negative = overdue, 0 = due today.
const daysOut = (ymd) => {
  const d = parseYmd(ymd);
  return d ? Math.round((d - startOfToday()) / 86400000) : null;
};
// Colours track the STATUSES palette so the row reads as one thing.
const dueMeta = (ymd, status) => {
  const n = daysOut(ymd);
  if (n === null) return null;
  const label = parseYmd(ymd).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  if (status === 'done') return { label, color: '#8b94a3', bg: '#f2f4f7', note: '' };
  if (n < 0) return { label, color: '#a12020', bg: '#fdecec', note: `${Math.abs(n)}d overdue` };
  if (n === 0) return { label, color: '#a12020', bg: '#fdecec', note: 'today' };
  if (n <= 7) return { label, color: '#8a5a00', bg: '#fff4dd', note: `${n}d` };
  return { label, color: '#5a6272', bg: '#eef2f6', note: '' };
};

// The five facts that make the case, and the four risks that decide it.
const FACTS = [
  { v: '42,595', l: 'paying households within 250 miles', s: '138,486 orders · $22.2M spend', img: /event.?tent|tent/ },
  { v: '$3.17M', l: 'land basis for the assemblage', s: '~12–14.5 acres, survey pending', img: /parcel|outline/ },
  { v: 'Resolved', l: 'zoning & Critical Area', s: 'Tourist Maritime · right side exempt' },
  { v: '#1', l: 'FEMA BRIC score, nationally', s: '$36.2M · parcels inside the boundary', img: /water.?tower|harbor/ },
  { v: 'On site', l: 'Governor Moore visited', s: 'Publicly supportive', img: /facebook post by governor/ },
];

const CAT_ORDER = ['Overview', 'Project', 'Site', 'Market', 'Program', 'Financial',
  'Capital', 'Approvals', 'Research', 'Package', 'Team'];

const imgUrl = (key, edge) => key
  ? (edge
    ? `/.netlify/functions/r2-resize?key=${encodeURIComponent(key)}&edge=${edge}`
    : `/.netlify/functions/r2-image?key=${encodeURIComponent(key)}`)
  : '';

// Prefer a wide rendering for the hero; fall back to whatever is first.
const pickHero = (list) => list.find((a) => (a.tags || []).includes('renderings')
  && (a.width || 0) > (a.height || 0)) || list[0] || null;

const hasTag = (a, t) => (a.tags || []).includes(t);
const textOf = (a) => `${a.suggested_name || ''} ${a.alt_text || ''}`.toLowerCase();
// Find one asset by a loose name/alt match, so the page keeps working as the
// library grows and ids change.
const findAsset = (list, re) => list.find((a) => re.test(textOf(a))) || null;

// Vision groups, in order. Anything tagged 7th-street that matches none of
// these falls into the catch-all so nothing is silently dropped.
const VISION_GROUPS = [
  { key: 'renderings', label: 'Renderings — Becker Morgan' },
  { key: 'site-plan', label: 'Site plan & concept' },
  { key: 'governor-moore', label: 'Governor Moore on site' },
  { key: 'tourism-deck', label: 'Crisfield & the experience' },
];

// Which imagery belongs alongside which document.
const DOC_IMAGERY = [
  [/^04-program\//, /rendering|site.?plan|marina|pavilion|aerial/],
  [/^02-site\//, /aerial|site.?plan|parcel|harbor|waterfront|dock/],
  [/^07-approvals\//, /governor|site.?plan|board/],
  [/^03-market\//, /tent|harbor|waterfront|sunset|dock|boat|crab|oyster|paddle|spa/],
  [/^research\//, /crisfield|waterfront|harbor|tower|dock|aerial/],
  [/^06-capital\//, /aerial|parcel|rendering/],
  [/./, /rendering|aerial|harbor|waterfront/],
];

export default function SeventhStreetBrain() {
  const [data, setData] = useState({ items: [], notes: [] });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('roadmap');
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(null); // phase name or 'new'
  const [draft, setDraft] = useState({ title: '', detail: '', owner: '', kind: 'task', due_date: '' });
  const [dateEdit, setDateEdit] = useState(null);
  const [note, setNote] = useState('');
  const [docs, setDocs] = useState([]);
  const [openDoc, setOpenDoc] = useState(null);
  const [docLoading, setDocLoading] = useState(false);
  const [gallery, setGallery] = useState([]);
  const [lightbox, setLightbox] = useState(null);

  const user = getCurrentUser();
  const me = user?.staffName || '';

  const load = useCallback(async () => {
    try {
      setErr('');
      const d = await neonQuery('ss_data', {});
      setData({ items: d.items || [], notes: d.notes || [] });
    } catch (e) {
      setErr(e.message || 'Could not load Seventh Street data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    neonQuery('media_assets_list', { tagsAll: ['7th-street'], limit: 60 })
      .then((rows) => setGallery(Array.isArray(rows) ? rows : (rows?.rows || [])))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => { if (e.key === 'Escape') setLightbox(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox]);

  useEffect(() => {
    if (tab !== 'docs' || docs.length) return;
    neonQuery('ss_docs_list', {})
      .then((rows) => setDocs(Array.isArray(rows) ? rows : []))
      .catch((e) => setErr(e.message));
  }, [tab, docs.length]);

  const readDoc = async (path) => {
    setDocLoading(true);
    setOpenDoc({ path, title: path, body: '' });
    try {
      const d = await neonQuery('ss_doc_get', { path });
      setOpenDoc(d.doc || { path, title: path, body: '_Not found._' });
    } catch (e) {
      setOpenDoc({ path, title: path, body: `_Could not load: ${e.message}_` });
    } finally { setDocLoading(false); }
  };

  const save = async (params) => {
    setSaving(true);
    try {
      await neonQuery('ss_item_upsert', params);
      await load();
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  };

  const cycleStatus = (item) => save({ id: item.id, status: nextStatus(item.status) });

  const removeItem = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    setSaving(true);
    try { await neonQuery('ss_item_delete', { id: item.id }); await load(); }
    catch (e) { setErr(e.message); } finally { setSaving(false); }
  };

  const addItem = async (phase, kind) => {
    if (!draft.title.trim()) return;
    await save({
      kind: kind || draft.kind, phase: phase || '', title: draft.title.trim(),
      detail: draft.detail.trim(), owner: draft.owner, due_date: draft.due_date,
      sort_order: 500,
    });
    setDraft({ title: '', detail: '', owner: '', kind: 'task', due_date: '' });
    setAdding(null);
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try {
      await neonQuery('ss_note_add', { body: note.trim(), author: me });
      setNote(''); await load();
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  };

  const phases = data.items.filter((i) => i.kind === 'phase');
  const tasks = data.items.filter((i) => i.kind === 'task');
  const questions = data.items.filter((i) => i.kind === 'question');
  const risks = data.items.filter((i) => i.kind === 'risk');

  const openCount = [...tasks, ...questions].filter((i) => i.status !== 'done').length;

  const StatusChip = ({ item }) => {
    const m = statusMeta(item.status);
    const Icon = m.icon;
    return (
      <button
        onClick={() => cycleStatus(item)}
        title="Click to change status"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer',
          background: m.bg, color: m.color, border: `1px solid ${m.color}33`,
          borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: 0.4, whiteSpace: 'nowrap',
        }}
      >
        <Icon size={12} /> {m.label}
      </button>
    );
  };

  const ItemRow = ({ item, showPhase }) => (
    <div style={{
      display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 0',
      borderBottom: '1px solid #eef1f4',
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, color: 'var(--navy)', marginBottom: 2 }}>
          {item.title}
        </div>
        {item.detail && (
          <div style={{ fontSize: 13, color: '#5a6272', lineHeight: 1.5 }}>{item.detail}</div>
        )}
        <div style={{ display: 'flex', gap: 10, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {item.owner && (
            <span style={{
              fontSize: 11, fontWeight: 700, color: 'var(--orange)',
              textTransform: 'uppercase', letterSpacing: 0.4,
            }}>{item.owner}</span>
          )}
          {dateEdit === item.id ? (
            <input
              type="date" className="form-input" autoFocus defaultValue={item.due_date || ''}
              onBlur={() => setDateEdit(null)}
              onChange={(e) => {
                if (!e.target.value) return;
                setDateEdit(null);
                save({ id: item.id, due_date: e.target.value });
              }}
              style={{ width: 150, padding: '2px 6px', fontSize: 12 }}
            />
          ) : (() => {
            const d = dueMeta(item.due_date, item.status);
            return (
              <button
                onClick={() => setDateEdit(item.id)}
                title={item.due_date ? 'Change due date' : 'Set a due date'}
                style={{
                  border: d ? 'none' : '1px dashed #c9d1da',
                  background: d ? d.bg : 'none',
                  color: d ? d.color : '#b9c0cb',
                  borderRadius: 20, padding: '2px 8px', fontSize: 11,
                  fontWeight: d ? 700 : 500, cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >
                {d ? `Due ${d.label}${d.note ? ` · ${d.note}` : ''}` : '+ due date'}
              </button>
            );
          })()}
          {showPhase && item.phase && (
            <span style={{ fontSize: 11, color: '#8b94a3' }}>{item.phase}</span>
          )}
          {item.link && (
            <a href={item.link} target="_blank" rel="noreferrer"
               style={{ fontSize: 11, color: 'var(--orange)', display: 'inline-flex', gap: 3 }}>
              link <ExternalLink size={11} />
            </a>
          )}
        </div>
      </div>
      <StatusChip item={item} />
      <button onClick={() => removeItem(item)} title="Delete"
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#b9c0cb', padding: 4 }}>
        <Trash2 size={14} />
      </button>
    </div>
  );

  const AddForm = ({ phase, kind, onCancel }) => (
    <div style={{ padding: 12, background: '#f7f9fb', borderRadius: 6, marginTop: 10 }}>
      <input className="form-input" placeholder="What needs doing?" value={draft.title}
        onChange={(e) => setDraft({ ...draft, title: e.target.value })}
        style={{ marginBottom: 8 }} autoFocus />
      <input className="form-input" placeholder="Detail (optional)" value={draft.detail}
        onChange={(e) => setDraft({ ...draft, detail: e.target.value })}
        style={{ marginBottom: 8 }} />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <select className="form-select" value={draft.owner}
          onChange={(e) => setDraft({ ...draft, owner: e.target.value })}
          style={{ maxWidth: 200 }}>
          {OWNERS.map((o) => <option key={o} value={o}>{o || 'Unassigned'}</option>)}
        </select>
        <input type="date" className="form-input" value={draft.due_date}
          onChange={(e) => setDraft({ ...draft, due_date: e.target.value })}
          title="Due date (optional)" style={{ maxWidth: 160 }} />
        <button className="btn btn-primary" onClick={() => addItem(phase, kind)} disabled={saving}>
          Add
        </button>
        <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );

  if (loading) {
    return <div className="page"><div style={{ padding: 40, color: '#8b94a3' }}>Loading Seventh Street…</div></div>;
  }

  return (
    <div className="page">
      <div className="breadcrumb">
        <a href="#seventh-street">Seventh Street</a>
        <span> / </span>
        <span>Seventh Street Brain</span>
      </div>

      {/* Hero */}
      {(() => {
        const hero = pickHero(gallery);
        return (
          <div style={{
            position: 'relative', borderRadius: 10, overflow: 'hidden',
            minHeight: 230, background: 'var(--navy)', marginBottom: 20,
            display: 'flex', alignItems: 'flex-end',
          }}>
            {hero && (
              <img src={imgUrl(hero.r2_original_key, 1800)} alt=""
                style={{
                  position: 'absolute', inset: 0, width: '100%', height: '100%',
                  objectFit: 'cover', objectPosition: 'center 55%',
                }} />
            )}
            <div style={{
              position: 'absolute', inset: 0,
              background: 'linear-gradient(180deg, rgba(0,57,92,.15) 0%, rgba(0,57,92,.55) 55%, rgba(0,57,92,.92) 100%)',
            }} />
            <div style={{
              position: 'relative', padding: '28px 26px 22px', width: '100%',
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
              gap: 16, flexWrap: 'wrap',
            }}>
              <div>
                <div style={{
                  color: 'var(--peach)', fontSize: 11, fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: 1.6, marginBottom: 6,
                }}>Crab Place · Crab &amp; Cruise · Davis Strategic</div>
                <h1 style={{ margin: 0, color: '#fff', fontSize: 34, lineHeight: 1.1 }}>
                  Seventh Street Brain
                </h1>
                <div style={{
                  color: 'rgba(255,255,255,.85)', fontSize: 13, marginTop: 8,
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  <MapPin size={13} /> Crisfield, MD · 37.9838, −75.8605 · Daugherty Creek
                </div>
              </div>
              <button className="btn" onClick={load} disabled={saving}
                style={{
                  background: 'rgba(255,255,255,.15)', color: '#fff',
                  border: '1px solid rgba(255,255,255,.35)', backdropFilter: 'blur(4px)',
                }}>
                <RefreshCw size={14} style={{ marginRight: 6, verticalAlign: -2 }} /> Refresh
              </button>
            </div>
          </div>
        );
      })()}

      {err && (
        <div style={{
          background: '#fdecec', color: '#a12020', padding: '10px 14px',
          borderRadius: 6, margin: '12px 0', fontSize: 13,
        }}>{err}</div>
      )}

      {/* The case, in five facts */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
        gap: 12, margin: '20px 0',
      }}>
        {FACTS.map((f) => {
          const shot = f.img ? findAsset(gallery, f.img) : null;
          return (
            <div key={f.l} className="card"
              style={{ padding: '14px 16px', position: 'relative', overflow: 'hidden' }}>
              <div style={{ paddingRight: shot ? 54 : 0 }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--orange)' }}>{f.v}</div>
                <div style={{ fontSize: 13, color: 'var(--navy)', fontWeight: 600, marginTop: 2 }}>{f.l}</div>
                <div style={{ fontSize: 11, color: '#8b94a3', marginTop: 3 }}>{f.s}</div>
              </div>
              {shot && (
                <button onClick={() => setLightbox(shot)} className="ss-fact-thumb"
                  title={`View — ${shot.alt_text || shot.suggested_name || ''}`}
                  style={{
                    position: 'absolute', top: 12, right: 12, width: 44, height: 44,
                    borderRadius: 6, overflow: 'hidden', padding: 0, cursor: 'zoom-in',
                    border: '1px solid #e4e9ee', background: '#f7f9fb',
                  }}>
                  <img src={imgUrl(shot.r2_thumb_key)} alt="" loading="lazy"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          ['roadmap', `Roadmap (${openCount} open)`],
          ['vision', `Vision${gallery.length ? ` (${gallery.length})` : ''}`],
          ['questions', `Open questions (${questions.filter((q) => q.status !== 'done').length})`],
          ['risks', `Risks (${risks.length})`],
          ['docs', `Documents${docs.length ? ` (${docs.length})` : ''}`],
          ['log', 'Activity log'],
        ].map(([k, label]) => (
          <button key={k} onClick={() => { setTab(k); setOpenDoc(null); setAdding(null); }}
            className={tab === k ? 'btn btn-primary' : 'btn btn-outline'}
            style={{ fontSize: 13 }}>{label}</button>
        ))}
      </div>

      {tab === 'roadmap' && (
        <div>
          {phases.map((ph) => {
            const kids = tasks.filter((t) => t.phase === ph.title);
            const done = kids.filter((k) => k.status === 'done').length;
            return (
              <div className="card" key={ph.id} style={{ marginBottom: 16 }}>
                <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                  <div>
                    <span style={{ fontWeight: 700, color: 'var(--navy)' }}>{ph.title}</span>
                    {ph.detail && <div style={{ fontSize: 12, color: '#8b94a3', marginTop: 2 }}>{ph.detail}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    {kids.length > 0 && (
                      <span style={{ fontSize: 12, color: '#8b94a3' }}>{done}/{kids.length}</span>
                    )}
                    <StatusChip item={ph} />
                  </div>
                </div>
                <div className="card-body">
                  {kids.length === 0 && (
                    <div style={{ fontSize: 13, color: '#8b94a3' }}>No items yet.</div>
                  )}
                  {kids.map((t) => <ItemRow key={t.id} item={t} />)}
                  {adding === ph.title
                    ? <AddForm phase={ph.title} kind="task" onCancel={() => setAdding(null)} />
                    : (
                      <button className="btn btn-ghost" style={{ marginTop: 10, fontSize: 13 }}
                        onClick={() => { setDraft({ title: '', detail: '', owner: '', kind: 'task' }); setAdding(ph.title); }}>
                        <Plus size={14} style={{ marginRight: 4 }} /> Add item
                      </button>
                    )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === 'vision' && (() => {
        const used = new Set();
        const groups = VISION_GROUPS.map((g) => {
          const items = gallery.filter((a) => hasTag(a, g.key) && !used.has(a.id));
          items.forEach((a) => used.add(a.id));
          return { ...g, items };
        });
        const rest = gallery.filter((a) => !used.has(a.id));
        if (rest.length) groups.push({ key: 'rest', label: 'Crisfield & the site from the air', items: rest });

        const Tile = ({ a, ratio = '4 / 3' }) => (
          <button onClick={() => setLightbox(a)} title={a.alt_text || a.suggested_name || ''}
            style={{
              padding: 0, border: '1px solid #e4e9ee', borderRadius: 8, overflow: 'hidden',
              cursor: 'zoom-in', background: '#f7f9fb', aspectRatio: ratio,
            }}>
            <img src={imgUrl(a.r2_thumb_key)} alt={a.alt_text || ''} loading="lazy"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          </button>
        );

        const tent = findAsset(gallery, /event.?tent|tent/);
        return (
          <div>
            {tent && (
              <div className="card" style={{ marginBottom: 16 }}>
                <div className="card-header">The proof — roughly 800 people, under a tent, on this site</div>
                <div className="card-body" style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  <button onClick={() => setLightbox(tent)}
                    style={{
                      padding: 0, border: 'none', borderRadius: 8, overflow: 'hidden',
                      cursor: 'zoom-in', flex: '1 1 420px', maxWidth: 620, background: '#f7f9fb',
                    }}>
                    <img src={imgUrl(tent.r2_original_key, 1200)} alt={tent.alt_text || ''}
                      style={{ width: '100%', display: 'block' }} />
                  </button>
                  <div style={{ flex: '1 1 260px', fontSize: 13, color: '#5a6272', lineHeight: 1.6 }}>
                    The DJ Jazzy Jeff event drew around 800 attendees under a tent at Somers Cove,
                    across the street from the assemblage — with no hotel in town.
                    <div style={{ marginTop: 10, color: 'var(--navy)', fontWeight: 600 }}>
                      “We never had a hotel when we put 800 people in town. They get in where they can fit in.”
                    </div>
                    <div style={{ marginTop: 10 }}>
                      It is the clearest evidence that the demand exists and that lodging is the constraint.
                      Attendance, ticket revenue and spend per head still need documenting.
                    </div>
                  </div>
                </div>
              </div>
            )}
            {groups.map((g) => g.items.length ? (
              <div className="card" key={g.key} style={{ marginBottom: 16 }}>
                <div className="card-header">
                  {g.label} <span style={{ color: '#8b94a3', fontWeight: 400 }}>· {g.items.length}</span>
                </div>
                <div className="card-body">
                  <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))' }}>
                    {g.items.map((a) => <Tile key={a.id} a={a} />)}
                  </div>
                </div>
              </div>
            ) : null)}
            {gallery.length === 0 && (
              <div className="card"><div className="card-body" style={{ color: '#8b94a3', fontSize: 13 }}>
                No images tagged <code>7th-street</code> yet.
              </div></div>
            )}
          </div>
        );
      })()}

      {lightbox && (
        <div onClick={() => setLightbox(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,20,35,.92)', zIndex: 3000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 30,
            cursor: 'zoom-out',
          }}>
          <div style={{ maxWidth: '92vw', maxHeight: '92vh', textAlign: 'center' }}>
            <img src={imgUrl(lightbox.r2_original_key, 1800)} alt={lightbox.alt_text || ''}
              style={{ maxWidth: '92vw', maxHeight: '82vh', objectFit: 'contain', borderRadius: 6 }} />
            <div style={{ color: 'rgba(255,255,255,.9)', fontSize: 13, marginTop: 12 }}>
              {lightbox.alt_text || lightbox.suggested_name}
            </div>
            <div style={{ color: 'rgba(255,255,255,.45)', fontSize: 11, marginTop: 4 }}>
              Click anywhere or press Esc to close
            </div>
          </div>
        </div>
      )}

      {tab === 'questions' && (
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Open questions — grouped by who can answer</span>
          </div>
          <div className="card-body">
            {OWNERS.filter((o) => o).map((owner) => {
              const qs = questions.filter((q) => q.owner === owner);
              if (!qs.length) return null;
              return (
                <div key={owner} style={{ marginBottom: 18 }}>
                  <div style={{
                    fontSize: 12, fontWeight: 700, color: 'var(--orange)',
                    textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4,
                  }}>Waiting on {owner}</div>
                  {qs.map((q) => <ItemRow key={q.id} item={q} showPhase />)}
                </div>
              );
            })}
            {questions.filter((q) => !q.owner).length > 0 && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#8b94a3', textTransform: 'uppercase', marginBottom: 4 }}>Unassigned</div>
                {questions.filter((q) => !q.owner).map((q) => <ItemRow key={q.id} item={q} showPhase />)}
              </div>
            )}
            {adding === 'question'
              ? <AddForm phase="" kind="question" onCancel={() => setAdding(null)} />
              : (
                <button className="btn btn-ghost" style={{ marginTop: 10, fontSize: 13 }}
                  onClick={() => { setDraft({ title: '', detail: '', owner: '', kind: 'question' }); setAdding('question'); }}>
                  <Plus size={14} style={{ marginRight: 4 }} /> Add question
                </button>
              )}
          </div>
        </div>
      )}

      {tab === 'risks' && (
        <div className="card">
          <div className="card-header">
            <AlertTriangle size={15} style={{ marginRight: 6, verticalAlign: -2, color: 'var(--orange)' }} />
            The risks that decide this project
          </div>
          <div className="card-body">
            {risks.map((r) => <ItemRow key={r.id} item={r} />)}
            {adding === 'risk'
              ? <AddForm phase="Risk" kind="risk" onCancel={() => setAdding(null)} />
              : (
                <button className="btn btn-ghost" style={{ marginTop: 10, fontSize: 13 }}
                  onClick={() => { setDraft({ title: '', detail: '', owner: '', kind: 'risk' }); setAdding('risk'); }}>
                  <Plus size={14} style={{ marginRight: 4 }} /> Add risk
                </button>
              )}
          </div>
        </div>
      )}

      {tab === 'docs' && !openDoc && (
        <div className="card">
          <div className="card-header">Document library</div>
          <div className="card-body">
            {docs.length === 0 && (
              <div style={{ fontSize: 13, color: '#8b94a3' }}>Loading documents…</div>
            )}
            {CAT_ORDER.map((cat) => {
              const inCat = docs.filter((d) => d.category === cat);
              if (!inCat.length) return null;
              return (
                <div key={cat} style={{ marginBottom: 20 }}>
                  <div style={{
                    fontSize: 12, fontWeight: 700, color: 'var(--orange)',
                    textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8,
                  }}>{cat}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 8 }}>
                    {inCat.map((d) => (
                      <button key={d.path} onClick={() => readDoc(d.path)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px',
                          border: '1px solid #e4e9ee', borderRadius: 6, color: 'var(--navy)',
                          background: '#fff', cursor: 'pointer', fontSize: 13, textAlign: 'left',
                          fontFamily: 'inherit',
                        }}>
                        <FileText size={14} style={{ color: 'var(--orange)', flexShrink: 0 }} />
                        <span style={{ flex: 1 }}>{d.title}</span>
                        <span style={{ fontSize: 11, color: '#b9c0cb' }}>
                          {Math.max(1, Math.round((d.bytes || 0) / 1024))} KB
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === 'docs' && openDoc && (
        <div className="card">
          <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="btn btn-ghost" onClick={() => setOpenDoc(null)} style={{ fontSize: 13 }}>
              <ArrowLeft size={14} style={{ marginRight: 4 }} /> All documents
            </button>
            <span style={{ color: '#8b94a3', fontSize: 12 }}>{openDoc.path}</span>
          </div>
          <div className="card-body">
            {docLoading && <div style={{ color: '#8b94a3', fontSize: 13 }}>Loading…</div>}
            {(() => {
              const rule = DOC_IMAGERY.find(([p]) => p.test(openDoc.path));
              const shots = rule
                ? gallery.filter((a) => rule[1].test(textOf(a))).slice(0, 6)
                : [];
              if (shots.length < 2) return null;
              return (
                <div style={{ marginBottom: 22 }}>
                  <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
                    {shots.map((a) => (
                      <button key={a.id} onClick={() => setLightbox(a)}
                        title={a.alt_text || a.suggested_name || ''}
                        style={{
                          flex: '0 0 auto', width: 132, height: 88, padding: 0,
                          border: '1px solid #e4e9ee', borderRadius: 6, overflow: 'hidden',
                          cursor: 'zoom-in', background: '#f7f9fb',
                        }}>
                        <img src={imgUrl(a.r2_thumb_key)} alt="" loading="lazy"
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                      </button>
                    ))}
                  </div>
                  <div style={{ fontSize: 11, color: '#b9c0cb', marginTop: 5 }}>
                    Related imagery — click to enlarge
                  </div>
                </div>
              );
            })()}
            <div className="ss-doc" style={{ maxWidth: 900, lineHeight: 1.65, color: '#3a4150' }}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{openDoc.body || ''}</ReactMarkdown>
            </div>
          </div>
        </div>
      )}

      {tab === 'log' && (
        <div className="card">
          <div className="card-header">
            <MessageSquare size={15} style={{ marginRight: 6, verticalAlign: -2 }} />
            Activity log
          </div>
          <div className="card-body">
            <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
              <input className="form-input" placeholder="Add an update…" value={note}
                onChange={(e) => setNote(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') addNote(); }} />
              <button className="btn btn-primary" onClick={addNote} disabled={saving || !note.trim()}>
                Post
              </button>
            </div>
            {data.notes.length === 0 && (
              <div style={{ fontSize: 13, color: '#8b94a3' }}>No updates yet.</div>
            )}
            {data.notes.map((n) => (
              <div key={n.id} style={{ padding: '10px 0', borderBottom: '1px solid #eef1f4' }}>
                <div style={{ fontSize: 14, color: 'var(--navy)' }}>{n.body}</div>
                <div style={{ fontSize: 11, color: '#8b94a3', marginTop: 3 }}>
                  {n.author || 'Unknown'} · {new Date(n.created_at).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
